import { TriangleAlertIcon } from "lucide-react";

import { CopyButton } from "@/components/copy-button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SecretRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  secret: string;
  /** Extra context, e.g. the token name and its scopes. */
  children?: React.ReactNode;
}

/**
 * Shared by CreateToken and RotateWebhookSecret. Both RPCs return plaintext
 * exactly once, so the copy has to be blunt about it.
 */
export function SecretRevealDialog({
  open,
  onOpenChange,
  title,
  description,
  secret,
  children,
}: SecretRevealDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Deliberately allow dismissal, but never re-open with the same value:
        // the caller clears its stored secret in onOpenChange(false).
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertTitle>You will not see this again</AlertTitle>
          <AlertDescription>
            The server stores only a hash. Once you close this dialog the plaintext is gone for
            good; the only recovery is to issue a new one.
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border bg-muted/50 p-3">
          <code className="block font-mono text-sm break-all select-all">{secret}</code>
        </div>

        {children}

        <DialogFooter className="sm:justify-between">
          <CopyButton value={secret} label="Copy to clipboard" variant="secondary" />
          <Button type="button" onClick={() => onOpenChange(false)}>
            I have stored it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
